// File: src/components/AnalysisPanel/ProductResultCard.tsx

import React, { useState } from "react";
import styles from "./ProductResultCard.module.css";
import { toProductHref, linkTarget } from "../../utils/url";

interface Product {
    id: string;
    score: number;
    category: string;
    brand: string;
    normalized_color: string;
    sku: string;
    image_url: string;
    product_url: string;
    description: string;
    price: number;
    instock: boolean;
    features: string[];
    benefits: string[];
    supported_features: string[];
    unsupported_features: string[];
    goldlist: boolean;
    nrvlist: boolean;
    title: string;
}

interface Props {
    product: Product;
}

export const ProductResultCard: React.FC<Props> = ({ product }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <div className={`${styles.card} ${isExpanded ? styles.expanded : ""}`}>
            <div className={styles.header} onClick={() => setIsExpanded(!isExpanded)} style={{ cursor: "pointer" }}>
                <img
                    src={product.image_url}
                    alt={product.title}
                    className={styles.productImage}
                    onError={e => {
                        (e.target as HTMLImageElement).style.display = "none";
                    }}
                />
                <div className={styles.basicInfo}>
                    <h3 className={styles.title}>{product.title}</h3>
                    <p className={styles.price}>{product.price.toFixed(2)}€</p>
                </div>
                <div className={styles.expandIcon}>{isExpanded ? "▼" : "▶"}</div>
            </div>

            {isExpanded && (
                <div className={styles.details}>
                    <div className={styles.detailRow}>
                        <span className={styles.label}>Brand:</span>
                        <span>{product.brand}</span>
                    </div>
                    <div className={styles.detailRow}>
                        <span className={styles.label}>SKU:</span>
                        <span>{product.sku}</span>
                    </div>
                    <div className={styles.detailRow}>
                        <span className={styles.label}>Category:</span>
                        <span>{product.category}</span>
                    </div>
                    <div className={styles.detailRow}>
                        <span className={styles.label}>Color:</span>
                        <span>{product.normalized_color}</span>
                    </div>
                    <div className={styles.detailRow}>
                        <span className={styles.label}>In Stock:</span>
                        <span className={product.instock ? styles.booleanYes : styles.booleanNo}>{product.instock ? "Yes" : "No"}</span>
                    </div>
                    <div className={styles.detailRow}>
                        <span className={styles.label}>Score:</span>
                        <span>{product.score.toFixed(2)}</span>
                    </div>
                    <div className={styles.detailRow}>
                        <span className={styles.label}>URL:</span>
                        {(() => {
                            const href = toProductHref(product.product_url, product.sku);
                            const target = linkTarget();
                            return (
                                <a href={href} target={target} rel={target === "_blank" ? "noopener noreferrer" : undefined}>
                                    View Product
                                </a>
                            );
                        })()}
                    </div>

                    {/* Features section */}
                    {(product.features.length > 0 || product.benefits.length > 0) && (
                        <div className={styles.featuresSection}>
                            {product.features.length > 0 && (
                                <div style={{ marginBottom: "12px" }}>
                                    <div className={styles.label}>Features:</div>
                                    <div className={styles.featuresList}>
                                        {product.features.map((feature, idx) => (
                                            <span key={idx} className={styles.featureChip}>
                                                {feature}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {product.benefits.length > 0 && (
                                <div>
                                    <div className={styles.label}>Benefits:</div>
                                    <div className={styles.featuresList}>
                                        {product.benefits.map((benefit, idx) => (
                                            <span key={idx} className={styles.featureChip}>
                                                {benefit}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Description */}
                    {product.description && (
                        <div className={styles.featuresSection}>
                            <div className={styles.label}>Description:</div>
                            <p style={{ margin: "8px 0 0 0", color: "#e5e5e5", lineHeight: "1.6" }}>{product.description}</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
